import { Button, Dialog, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';

import { useWorkspace } from '@/contexts/useWorkspace';

import { ButtonWithIcon } from '../buttons/buttonWithIcon';
import { LoaderButton } from '../buttons/loaderButton';
import { Icon } from '../shared/icon';

interface ConfirmationDialogProps {
  isOpen: boolean;
  title: React.ReactNode;
  description: React.ReactNode;
  onCancel: () => void;
  onConfirm: () => Promise<void> | void;
  buttonIconClass?: string;
  buttonTitle: string;
  cancelButtonTitle?: string;
  confirmSnackbarText?: string;
}

export function ConfirmationDialog(props: ConfirmationDialogProps) {
  const [error, setError] = useState('');
  const { snackbar } = useWorkspace();
  const [confirmationInProgress, setConfirmationProgress] = useState(false);

  const onConfirm = async () => {
    setError('');
    setConfirmationProgress(true);
    try {
      await props.onConfirm();
      if (props.confirmSnackbarText) {
        snackbar.showMessage(props.confirmSnackbarText);
      }
    } catch (error) {
      const err = error as Error;
      setError(err.message);
      throw err;
    } finally {
      setConfirmationProgress(false);
    }
  };

  // reset error message when dialog is closed
  useEffect(() => {
    if (props.isOpen) {
      setError('');
      setConfirmationProgress(false);
    }
  }, [props.isOpen]);

  return (
    <Dialog
      open={props.isOpen}
      slotProps={{
        paper: {
          sx: {
            width: 'max-content',
            maxWidth: '840px',
            minWidth: '600px',
            height: 'max-content',
            maxHeight: '90vh',
            borderRadius: '16px',
          },
        },
      }}
    >
      <Stack data-testid='confirmation-dialog'>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          padding='16px 24px'
          paddingBottom='0'
        >
          <div></div>
          <Icon
            onClick={props.onCancel}
            iconClass='material-symbols-close-rounded'
          />
        </Stack>
        <Stack
          alignItems='center'
          gap='12px'
          sx={{ px: '40px', textAlign: 'center', pb: '40px' }}
        >
          {typeof props.title === 'string' ? (
            <Typography variant='h2'>{props.title}</Typography>
          ) : (
            props.title
          )}
          <Typography
            maxWidth='70%'
            variant='body1'
            className='mb-[12px]'
            lineHeight={'20px'}
          >
            {props.description}
          </Typography>
          <Stack direction='row' gap='12px'>
            <Button onClick={props.onCancel}>
              {props.cancelButtonTitle ?? 'Cancel'}
            </Button>
            {!confirmationInProgress ? (
              <ButtonWithIcon
                onClick={() => void onConfirm()}
                icon={props.buttonIconClass}
                text={props.buttonTitle}
                variant='outlined'
              />
            ) : (
              <LoaderButton />
            )}
          </Stack>
        </Stack>
        {error && (
          <Typography padding='0 0 16px 24px' color='error' variant='body1'>
            {error}
          </Typography>
        )}
      </Stack>
    </Dialog>
  );
}
